import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Copy, Check, ExternalLink, ChevronDown, ChevronRight, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageShell, Stat } from "./PageShell";

function UrlCard({ label, url, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); toast.success(`${label} copied`); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">{label}</div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7" onClick={copy} data-testid={`${testid}-copy`}>
            {copied ? <Check className="h-3.5 w-3.5 text-[#00E65B]" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
          </Button>
          <a href={url} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" className="h-7 w-7" title="Open" data-testid={`${testid}-open`}><ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /></Button></a>
        </div>
      </div>
      <div className="font-mono text-sm break-all bg-surface border border-border rounded-sm px-3 py-2" data-testid={`${testid}-value`}>{url}</div>
    </div>
  );
}

export default function PlatformSingpass() {
  const [cfg, setCfg] = useState(null);
  const [showJwks, setShowJwks] = useState(false);

  const load = useCallback(() => {
    api.get("/sgverify/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <PageShell eyebrow="Platform Operations" title="Singpass Registration"
      subtitle="Copy these two URLs into the Singpass Developer Portal to register this app for MyInfo Business. Singpass fetches the JWKS to validate the signing & encryption keys, then issues a client_id (stored for the next phase)."
      testid="platform-singpass"
      actions={<Button variant="outline" onClick={load} data-testid="singpass-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>}>

      {!cfg ? (
        <div className="text-muted-foreground font-mono text-sm p-4">Loading Singpass configuration…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Environment" value={cfg.environment} tone="accent" />
            <Stat label="Public domain" value={cfg.public_domain || "— not set —"} />
            <Stat label="client_id" value={cfg.client_id || "pending"} tone={cfg.client_id ? "accent" : "amber"} sub={cfg.client_id ? "" : "issued by the portal after submission"} />
            <Stat label="Keys ready" value={cfg.ready ? "Yes" : "No"} tone={cfg.ready ? "accent" : "red"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <UrlCard label="Redirect URL" url={cfg.redirect_url} testid="singpass-redirect-url" />
            <UrlCard label="JWKS URL" url={cfg.jwks_url} testid="singpass-jwks-url" />
          </div>

          <div className="card-flat p-5" data-testid="singpass-checklist">
            <h2 className="font-display font-semibold text-lg mb-1 inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> JWKS requirements</h2>
            <p className="text-xs text-muted-foreground mb-3">Mirrors Singpass's rules — all must pass before you submit the JWKS URL.</p>
            <ul className="space-y-2">
              {cfg.checklist.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`singpass-check-${c.key}`}>
                  {c.ok ? <Check className="h-4 w-4 text-[#00E65B] shrink-0" /> : <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />}
                  <span className={c.ok ? "" : "text-red-400"}>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-flat p-4" data-testid="singpass-jwks-preview">
            <button className="flex items-center gap-1.5 text-sm font-medium" onClick={() => setShowJwks((v) => !v)} data-testid="singpass-jwks-toggle">
              {showJwks ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Live JWKS preview <Badge variant="outline" className="text-[9px] ml-1">{(cfg.jwks?.keys || []).length} keys</Badge>
            </button>
            {showJwks && (
              <pre className="mt-3 bg-surface border border-border rounded-sm p-3 text-[11px] font-mono overflow-x-auto max-h-[420px]" data-testid="singpass-jwks-json">{JSON.stringify(cfg.jwks, null, 2)}</pre>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground">Only public key material is published — private keys stay server-side and are never served. Sandbox keys; production keys &amp; the live MyInfo sign-in / KYB auto-fill flow come in later phases.</p>
        </>
      )}
    </PageShell>
  );
}
